// src/store/slices/coordinateTablesSlice.ts
/**
 * Coordinate Tables Redux Slice
 * 
 * Manages draggable x/y value tables linked to equations.
 * Phase 4: Coordinate Tables
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../types/redux';
import { CoordinateTable, Point2D } from '../../types/coordinateGrid';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

export interface CoordinateTablesState {
  tables: CoordinateTable[];
  nextId: number;
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: CoordinateTablesState = {
  tables: [],
  nextId: 1,
};

// =============================================================================
// REDUX SLICE
// =============================================================================

export const coordinateTablesSlice = createSlice({
  name: 'coordinateTables',
  initialState,
  reducers: {
    /**
     * Add a new coordinate table
     */
    addTable: (
      state,
      action: PayloadAction<{
        equationId?: string | null;
        xValues?: number[];
        position?: Point2D;
      }>
    ) => {
      const { equationId = null, xValues = [], position = { x: 20, y: 20 } } = action.payload;

      const newTable: CoordinateTable = {
        id: `table-${state.nextId}`,
        equationId,
        xValues: [...xValues],
        showYValues: true,
        showEquation: true,
        position,
        isCollapsed: false,
        columnWidth: 80,
      };

      state.tables.push(newTable);
      state.nextId++;
    },

    /**
     * Update a table's properties
     */
    updateTable: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<CoordinateTable>;
      }>
    ) => {
      const table = state.tables.find((t) => t.id === action.payload.id);
      if (table) {
        Object.assign(table, action.payload.updates);
      }
    },

    /**
     * Delete a table by ID
     */
    deleteTable: (state, action: PayloadAction<string>) => {
      state.tables = state.tables.filter((t) => t.id !== action.payload);
    },

    /**
     * Set table position (for dragging)
     */
    setTablePosition: (
      state,
      action: PayloadAction<{
        id: string;
        position: Point2D;
      }>
    ) => {
      const table = state.tables.find((t) => t.id === action.payload.id);
      if (table) {
        table.position = action.payload.position;
      }
    },

    /**
     * Add an x-value to a table
     */
    addXValue: (
      state,
      action: PayloadAction<{
        tableId: string;
        value: number;
      }>
    ) => {
      const table = state.tables.find((t) => t.id === action.payload.tableId);
      if (table) {
        // Avoid duplicates
        if (!table.xValues.includes(action.payload.value)) {
          table.xValues.push(action.payload.value);
          // Sort x-values
          table.xValues.sort((a, b) => a - b);
        }
      }
    },

    /**
     * Remove an x-value from a table by index
     */
    removeXValue: (
      state,
      action: PayloadAction<{
        tableId: string;
        index: number;
      }>
    ) => {
      const table = state.tables.find((t) => t.id === action.payload.tableId);
      if (table && action.payload.index >= 0 && action.payload.index < table.xValues.length) {
        table.xValues.splice(action.payload.index, 1);
      }
    },

    /**
     * Set all x-values for a table
     */
    setXValues: (
      state,
      action: PayloadAction<{
        tableId: string;
        values: number[];
      }>
    ) => {
      const table = state.tables.find((t) => t.id === action.payload.tableId);
      if (table) {
        table.xValues = [...action.payload.values].sort((a, b) => a - b);
      }
    },

    /**
     * Toggle y-values visibility
     */
    toggleYValuesVisibility: (state, action: PayloadAction<string>) => {
      const table = state.tables.find((t) => t.id === action.payload);
      if (table) {
        table.showYValues = !table.showYValues;
      }
    },

    /**
     * Toggle equation visibility
     */
    toggleEquationVisibility: (state, action: PayloadAction<string>) => {
      const table = state.tables.find((t) => t.id === action.payload);
      if (table) {
        table.showEquation = !table.showEquation;
      }
    },

    /**
     * Link a table to an equation
     */
    linkToEquation: (
      state,
      action: PayloadAction<{
        tableId: string;
        equationId: string | null;
      }>
    ) => {
      const table = state.tables.find((t) => t.id === action.payload.tableId);
      if (table) {
        table.equationId = action.payload.equationId;
      }
    },

    /**
     * Toggle table collapsed state
     */
    toggleTableCollapsed: (state, action: PayloadAction<string>) => {
      const table = state.tables.find((t) => t.id === action.payload);
      if (table) {
        table.isCollapsed = !table.isCollapsed;
      }
    },

    /**
     * Load tables state (for scene loading)
     */
    loadTablesState: (state, action: PayloadAction<Partial<CoordinateTablesState>>) => {
      Object.assign(state, { ...initialState, ...action.payload });
    },

    /**
     * Clear all tables
     */
    clearAllTables: (state) => {
      state.tables = [];
    },
  },
});

// Export actions
export const {
  addTable,
  updateTable,
  deleteTable,
  setTablePosition,
  addXValue,
  removeXValue,
  setXValues,
  toggleYValuesVisibility,
  toggleEquationVisibility,
  linkToEquation,
  toggleTableCollapsed,
  loadTablesState,
  clearAllTables,
} = coordinateTablesSlice.actions;

// Selectors
export const selectAllTables = (state: RootState) => state.coordinateTables.tables;
export const selectTableById = (id: string) => (state: RootState) =>
  state.coordinateTables.tables.find((t) => t.id === id);
export const selectTablesByEquation = (equationId: string) => (state: RootState) =>
  state.coordinateTables.tables.filter((t) => t.equationId === equationId);

export default coordinateTablesSlice.reducer;
